import React from 'react';
import PropTypes from 'prop-types';

import {
  Accordion, AccordionDetails, AccordionSummary, Box, Typography, Paper, Button, Divider, Link, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
} from 'views/components';
import {Translate, translate, MarkdownViewer} from 'views/module';

import moment from 'moment';

const InternalApplicationCard = (props) => {
  const {application, jobBackend} = props;
  const answers = application.answers || {};
  const hasLanguages = answers.languages;
  const hasCertifications = answers.certifications;

  const {statusColor, statusLabel} = React.useMemo(() => {
    switch (application.status) {
      case 'ACCEPTED':
        return {
          statusColor: '#80FFB3',
          statusLabel: translate('career.jobDetail.Accepted'),
        };
      case 'REJECTED':
        return {
          statusColor: '#767676',
          statusLabel: translate('career.jobDetail.Rejected'),
        };
      case 'PENDING':
      default:
        return {
          statusColor: '#FFD60A',
          statusLabel: translate('career.jobDetail.Pending'),
        };
    }
  }, [application.status]);

  const countTrue = (obj) => {
    if (!obj) return {count: 0, total: 0};
    const values = Object.values(obj);
    const total = values.length;
    const count = values.reduce((acc, v) => acc + (v === true ? 1 : 0), 0);
    return {count, total};
  };

  const langCounts = countTrue(answers.languages);
  const certCounts = countTrue(answers.certifications);


  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const handleAccept = (e) => {
    e.stopPropagation();
    setConfirmOpen(true);
  };

  const handleConfirmAccept = () => {
    setConfirmOpen(false);
    jobBackend.applications.acceptApplication(application);
  };

  const handleCancelAccept = () => {
    setConfirmOpen(false);
  };

  const handleDeny = (e) => {
    e.stopPropagation();
    return jobBackend.applications.denyApplication(application);
  };

  return (
    <>
      <Accordion>
        <AccordionSummary
          sx={{
            '.MuiAccordionSummary-content': {m: 0},
            'pb': 2,
            '.MuiAccordionSummary-expandIconWrapper': {
              alignSelf: 'flex-end',
              mb: 6,
              mr: 2,
            },
            'mt': 1,
          }}
        >
          <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
            <Box sx={{display: 'flex', alignItems: 'center', width: '100%'}}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{flex: 1, minWidth: 0}}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: statusColor,
                    borderRadius: 12,
                    px: 1.25,
                    py: 0.25,
                    fontWeight: 500,
                    fontSize: 14,
                    height: 24,
                  }}
                >
                  {statusLabel}
                </Box>
                <Link href={`/caregiver/${application.caregiverRn}`}>
                  <Typography variant="h6" sx={{cursor: 'pointer', fontWeight: 600}}>
                    {application.caregiver.name}
                  </Typography>
                </Link>
              </Stack>

              {/* Action Buttons */}
              <Box sx={{display: 'flex', flexDirection: 'row', gap: 1.5, mt: 1, flex: 'none'}}>
                <Button
                  onClick={handleDeny}
                  mode="contained"
                  sx={{
                    borderRadius: 24,
                    px: 2.25,
                    py: 1,
                    minWidth: 100,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Translate expression="career.jobDetail.DenyApplication" />
                </Button>
                <Button
                  onClick={handleAccept}
                  mode="contained"
                  sx={{
                    borderRadius: 24,
                    px: 2.25,
                    py: 1,
                    minWidth: 100,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Translate expression="career.jobDetail.AcceptApplication" />
                </Button>
              </Box>
            </Box>

            {/* Summary chips: counts only */}
            <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 1, gap: 1}}>
              {
                hasLanguages &&
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  <Translate expression="career.jobDetail.Summary.Languages" substitute={{count: langCounts.count, total: langCounts.total}} />
                </Box>
              }
              {
                hasCertifications &&
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  <Translate expression="career.jobDetail.Summary.Certifications" substitute={{count: certCounts.count, total: certCounts.total}} />
                </Box>
              }
              {
                answers.considerationConfirmations &&
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#f3f4f6',
                    color: '#6b7280',
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  <Translate
                    expression="career.jobDetail.Summary.Considerations"
                    substitute={{
                      count: countTrue(answers.considerationConfirmations).count,
                      total: countTrue(answers.considerationConfirmations).total,
                    }}
                  />
                </Box>
              }
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: answers.canTravelToLocation ? '#d4edda' : '#f8d7da',
                  color: '#333333',
                  border: `1px solid ${answers.canTravelToLocation ? '#c3e6cb' : '#f5c6cb'}`,
                  borderRadius: 12,
                  px: 1.5,
                  py: 0.5,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                <Translate
                  expression="career.jobDetail.Summary.CanTravel"
                  substitute={{
                    value: answers.canTravelToLocation ? translate('career.jobDetail.Summary.Yes') : translate('career.jobDetail.Summary.No'),
                  }}
                />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <Divider />
        <AccordionDetails>
          <Box sx={{display: 'grid', gap: 2, ml: 4}}>
            {
              (hasLanguages || hasCertifications) &&
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                {
                  hasLanguages &&
                  <Box sx={{mb: hasCertifications ? 1.5 : 0}}>
                    <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 1}}>
                      <Translate expression="career.jobDetail.Details.Languages" />
                    </Typography>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                      {
                        Object.entries(answers.languages).map(([k, v]) => {
                          const bg = v ? '#d4edda' : '#f8d7da';
                          const border = v ? '#c3e6cb' : '#f5c6cb';
                          const label = translate(`career.create.Requirements.LanguageOptions.${k}`) || k;
                          return (
                            <Box
                              key={k}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                backgroundColor: bg,
                                color: '#333333',
                                border: `1px solid ${border}`,
                                borderRadius: 12,
                                px: 1.25,
                                py: 0.25,
                              }}
                            >
                              {label}
                            </Box>
                          );
                        })
                      }
                    </Box>
                  </Box>
                }
                <Box>
                  <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 1}}>
                    <Translate expression="career.jobDetail.Details.Certifications" />
                  </Typography>
                  <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                    {
                      Object.entries(answers.certifications).map(([k, v]) => {
                        const bg = v ? '#d4edda' : '#f8d7da';
                        const border = v ? '#c3e6cb' : '#f5c6cb';
                        const label = translate(`career.create.Requirements.CertificationsOptions.${k}`) || k;
                        return (
                          <Box
                            key={k}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: bg,
                              color: '#333333',
                              border: `1px solid ${border}`,
                              borderRadius: 12,
                              px: 1.25,
                              py: 0.25,
                            }}
                          >
                            {label}
                          </Box>
                        );
                      })
                    }
                  </Box>
                </Box>
              </Paper>
            }

            {
              answers.considerationConfirmations &&
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 1}}>
                  <Translate expression="career.jobDetail.Details.Considerations" />
                </Typography>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                  {
                    Object.entries(answers.considerationConfirmations || {}).map(([k, v]) => {
                      const bg = v ? '#d4edda' : '#f8d7da';
                      const border = v ? '#c3e6cb' : '#f5c6cb';
                      const label = translate(`career.create.Considerations.Options.${k}`) || k;
                      return (
                        <Box
                          key={k}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: bg,
                            color: '#333333',
                            border: `1px solid ${border}`,
                            borderRadius: 12,
                            px: 1.25,
                            py: 0.25,
                          }}
                        >
                          {label}
                        </Box>
                      );
                    })
                  }
                </Box>
              </Paper>
            }
            {
              application.jobDetails.requirements.additionalConsiderations.blob &&
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 1}}>
                  <Translate expression="career.jobDetail.Details.AdditionalConsiderations" />
                </Typography>
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: answers.additionalConsiderationConfirmations ? '#d4edda' : '#f8d7da',
                      color: '#333333',
                      border: `1px solid ${answers.additionalConsiderationConfirmations ? '#c3e6cb' : '#f5c6cb'}`,
                      borderRadius: 12,
                      px: 1.25,
                      py: 0.25,
                    }}
                  >
                    <MarkdownViewer value={application.jobDetails.requirements.additionalConsiderations.blob} />
                  </Box>
                </Box>
              </Paper>
            }
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1.5,
                backgroundColor: '#fff',
                borderColor: '#e5e7eb',
              }}
            >
              <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 1}}>
                <Translate expression="career.jobDetail.Details.Travel" />
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: answers.canTravelToLocation ? '#d4edda' : '#f8d7da',
                  color: '#333333',
                  border: `1px solid ${answers.canTravelToLocation ? '#c3e6cb' : '#f5c6cb'}`,
                  borderRadius: 12,
                  px: 1.25,
                  py: 0.25,
                }}
              >
                <Translate
                  expression="career.jobDetail.Details.CanTravelToLocation"
                  substitute={{
                    value: answers.canTravelToLocation ? translate('career.jobDetail.Summary.Yes') : translate('career.jobDetail.Summary.No'),
                  }}
                />
              </Box>
            </Paper>

            {
              application.answers.selectedShifts.type !== 'LIVEIN_EVENT' &&
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                <Typography variant="subtitle1" sx={{fontWeight: 700, mb: 1}}>
                  <Translate expression="career.jobDetail.Details.ScheduleAvailability" />
                </Typography>
                <Box
                  sx={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    display: 'flex',
                  }}
                >
                  {
                    application.jobDetails.schedule.recurrenceRule.weeklySlots.map((slot, index) => {
                      const isAvailable = application.answers.selectedShifts.recurrenceRule.weeklySlots[index];
                      const startTime = moment(slot.startTime).format('h:mm A');
                      const endTime = moment(slot.endTime).format('h:mm A');
                      const dayMap = translate('career.jobDetail.Day');
                      return (
                        <Box
                          key={index}
                          sx={{
                            backgroundColor: isAvailable ? '#d4edda' : '#f8d7da',
                            borderColor: isAvailable ? '#c3e6cb' : '#f5c6cb',
                            borderWidth: 1,
                            borderStyle: 'solid',
                            borderRadius: 12,
                            alignItems: 'center',
                            mr: 1,
                            mb: 1,
                            display: 'inline-flex',
                            px: 1.5,
                            py: 0.5,
                            fontWeight: 600,
                            fontSize: 13,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 16,
                              color: '#333333',
                              textAlign: 'center',
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              lineHeight: 1.2,
                            }}
                          >
                            {`${dayMap[slot.day]}: ${startTime} - ${endTime}`}
                          </Typography>
                        </Box>
                      );
                    })
                  }
                </Box>
              </Paper>
            }
          </Box>
        </AccordionDetails>
      </Accordion>
      {/* Dialog render */}
      {
        confirmOpen &&
        <Dialog open={confirmOpen} onClose={handleCancelAccept}>
          <DialogTitle>
            <Translate expression="career.jobDetail.ConfirmAcceptTitle" />
          </DialogTitle>
          <DialogContent>
            <Typography>
              <Translate expression="career.jobDetail.ConfirmAcceptText" substitute={{name: application.caregiver.name}} />
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCancelAccept}
              mode="text"
              sx={{borderRadius: 24, px: 2.25, py: 1, minWidth: 100, alignItems: 'center', justifyContent: 'center'}}
            >
              <Translate expression="generic.No" />
            </Button>
            <Button
              onClick={handleConfirmAccept}
              mode="contained" color="primary"
              sx={{borderRadius: 24, px: 2.25, py: 1, minWidth: 100, alignItems: 'center', justifyContent: 'center'}}
            >
              <Translate expression="generic.Yes" />
            </Button>
          </DialogActions>
        </Dialog>
      }
    </>
  );
};

InternalApplicationCard.propTypes = {
  application: PropTypes.shape({
    jobRn: PropTypes.string.isRequired,
    caregiverRn: PropTypes.string.isRequired,
    caregiver: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    answers: PropTypes.shape({
      canTravelToLocation: PropTypes.bool.isRequired,
      careNeedConfirmations: PropTypes.object.isRequired,
      languages: PropTypes.objectOf(PropTypes.bool),
      certifications: PropTypes.objectOf(PropTypes.bool),
      considerationConfirmations: PropTypes.object,
      additionalConsiderationConfirmations: PropTypes.bool,
      selectedShifts: PropTypes.shape({
        type: PropTypes.string.isRequired,
        recurrenceRule: PropTypes.shape({
          weeklySlots: PropTypes.arrayOf(
              PropTypes.oneOfType([
                PropTypes.bool,
                PropTypes.oneOf([null]),
              ]),
          ).isRequired,
        }),
      }).isRequired,
    }).isRequired,
    jobDetails: PropTypes.shape({
      jobRn: PropTypes.string.isRequired,
      client: PropTypes.shape({
        clientRn: PropTypes.string,
        unregisteredClient: PropTypes.string,
      }).isRequired,
      createdAt: PropTypes.string.isRequired,
      description: PropTypes.shape({
        long: PropTypes.shape({
          blob: PropTypes.string.isRequired,
          editorType: PropTypes.string.isRequired,
        }).isRequired,
        short: PropTypes.string.isRequired,
      }).isRequired,
      location: PropTypes.shape({
        city: PropTypes.string.isRequired,
        neighborhood: PropTypes.string.isRequired,
      }).isRequired,
      price: PropTypes.shape({
        hourlyRate: PropTypes.number.isRequired,
      }).isRequired,
      requirements: PropTypes.shape({
        additionalConsiderations: PropTypes.shape({
          blob: PropTypes.string.isRequired,
          editorType: PropTypes.string.isRequired,
        }).isRequired,
        careNeeds: PropTypes.arrayOf(PropTypes.string).isRequired,
        certifications: PropTypes.arrayOf(PropTypes.string).isRequired,
        considerations: PropTypes.arrayOf(PropTypes.string).isRequired,
        languages: PropTypes.arrayOf(PropTypes.string).isRequired,
      }).isRequired,
      schedule: PropTypes.shape({
        recurrenceRule: PropTypes.shape({
          timezone: PropTypes.string.isRequired,
          weeklySlots: PropTypes.arrayOf(
              PropTypes.shape({
                day: PropTypes.string.isRequired,
                startTime: PropTypes.string.isRequired,
                endTime: PropTypes.string.isRequired,
              }),
          ).isRequired,
        }),
        type: PropTypes.string.isRequired,
      }).isRequired,
      startDate: PropTypes.shape({
        startTime: PropTypes.string.isRequired,
      }).isRequired,
      status: PropTypes.string.isRequired,
      updatedAt: PropTypes.string.isRequired,
    }).isRequired,
    status: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
  }).isRequired,
  jobBackend: PropTypes.object.isRequired,
};

export default InternalApplicationCard;
